import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Star, Download, ExternalLink, Heart } from 'lucide-react';
import { getSharedPrompts } from '../../../services/api';

const SharedPromptsBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [sharedPrompts, setSharedPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSharedPrompts();
  }, [user, refreshTrigger]);

  const loadSharedPrompts = async () => {
    if (!user?.id) return;
    
    try {
      const promptsData = await getSharedPrompts(user.id);
      setSharedPrompts(promptsData || []);
    } catch (error) {
      console.error('Failed to load shared prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 2;
      case 'medium':
        return 3;
      case 'large':
        return 6;
      default:
        return 3;
    }
  };

  const displayPrompts = sharedPrompts.slice(0, getDisplayCount());

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const getPermissionColor = (level) => {
    switch (level) {
      case 'owner':
        return 'bg-celadon-100 text-celadon-800 dark:bg-celadon-900/30 dark:text-celadon-300';
      case 'editor':
        return 'bg-celadon-50 text-celadon-700 dark:bg-celadon-900/20 dark:text-celadon-400';
      case 'reviewer':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      case 'commenter':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (sharedPrompts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Users className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">No shared prompts yet</p>
        <Link to="/shared-prompts" className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300">
          View all shared prompts
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Shared with You</span>
        </div>
        <Link 
          to="/shared-prompts"
          className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300"
        >
          View all
        </Link>
      </div>

      <div className="space-y-3 overflow-y-auto max-h-full">
        {displayPrompts.map((prompt) => (
          <div
            key={prompt.id}
            className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {prompt.name}
                  </h4>
                  {prompt.permission_level && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPermissionColor(prompt.permission_level)}`}>
                      {prompt.permission_level}
                    </span>
                  )}
                </div>
                
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  in {prompt.project_name} • by {prompt.project_owner_name}
                </p>

                {size !== 'small' && prompt.description && (
                  <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2 mb-2">
                    {prompt.description}
                  </p>
                )}

                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex items-center">
                    <span>{prompt.version_count} version{prompt.version_count !== '1' ? 's' : ''}</span>
                  </div>
                </div>
              </div>
              
              <Link
                to={`/prompts/${prompt.id}`}
                className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 flex-shrink-0"
                title="View prompt"
              >
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
        <Link
          to="/shared-prompts"
          className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300 font-medium"
        >
          View all shared prompts
        </Link>
      </div>
    </div>
  );
};

export default SharedPromptsBlock; 